import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { TwoFactorService } from '../api/services/TwoFactorService';
import { impersonationManager } from '../business/auth/ImpersonationManager';
import { accountSessionManager } from '../business/auth/AccountSessionManager';

interface TwoFactorStatus {
  isChecking: boolean;
  needsSetup: boolean;
}

// In-memory only — intentionally NOT sessionStorage.
// 2FA is a security gate: we must always verify with the API on fresh page load.
// The cache only prevents re-checking during in-session navigations.
let twoFactorCache: { userId: string; needsSetup: boolean } | null = null;

/**
 * Hook that checks whether the current user needs to set up 2FA.
 * Uses sessionStorage to resolve instantly on subsequent page loads within the same session,
 * while still validating in the background.
 */
export function useTwoFactorStatus(): TwoFactorStatus {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();

  const [isChecking, setIsChecking] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);
  const hasCheckedRef = useRef(false);

  useEffect(() => {
    const check = async () => {
      if (authLoading) return;

      // A switched-account session has no OIDC user on this origin, but it is a real session for a
      // real user row — and 2FA enforcement is global, so that row may not be enrolled. Deliberately
      // NOT treated like impersonation below: the wall must still apply here.
      const accountSessionActive = accountSessionManager.isActive();

      if (!accountSessionActive && (!isAuthenticated || !user)) {
        setIsChecking(false);
        return;
      }

      if (impersonationManager.isImpersonating()) {
        setIsChecking(false);
        return;
      }

      if (hasCheckedRef.current) return;
      hasCheckedRef.current = true;

      const userId = user?.sub || user?.profile?.sub || accountSessionManager.getSubject() || '';

      // Use in-memory cache for in-session navigations (not persisted across page loads)
      if (twoFactorCache && twoFactorCache.userId === userId) {
        setNeedsSetup(twoFactorCache.needsSetup);
        setIsChecking(false);
        return;
      }

      try {
        const status = await TwoFactorService.getApiAppTwoFactorGetStatus();
        const enabled = status.isEnabled ?? false;
        const enforced = status.isEnforced ?? false;
        const needs = !enabled && enforced;
        setNeedsSetup(needs);
        twoFactorCache = { userId, needsSetup: needs };
      } catch (error) {
        console.error('[TwoFactorStatus] Failed to check 2FA status:', error);
        setNeedsSetup(true);
      } finally {
        setIsChecking(false);
      }
    };

    check();
  }, [isAuthenticated, user, authLoading]);

  return { isChecking, needsSetup };
}
